'use strict';

const swatchSelector = require('../components/swatchSelector');

/**
 * Enforce no focus on elements with class js-no-focus.
 * Used both during slider init and on afterChange to keep
 * non-interactive cloned slides out of the tab order.
 */
const enforceNoFocus = ($context) => {
    $context.find('.js-no-focus').each(function () {
        this.setAttribute('tabindex', '-1');
    });
};

/**
 * Re-syncs the tabindex of focusable content inside each slide with its
 * current aria-hidden state. Slick's setSlideClasses() (which sets
 * aria-hidden) never touches tabindex on its own — only the one-time
 * initADA() does, so without this a slide can sit aria-hidden="true" while
 * its link/button stays keyboard-focusable (or vice versa) after any
 * transition.
 */
const syncSlideTabindex = ($slider) => {
    $slider.find('.slick-slide').each(function () {
        const $slide = $(this);
        const isHidden = $slide.attr('aria-hidden') === 'true';
        $slide.find('a, button, input, select').attr('tabindex', isHidden ? '-1' : '0');
    });
};

/**
 * Initialize sliders with Slick
 */
const initializeSliders = ($context) => {
    ($context || $('.js-slider')).each(function () {
        const $slider = $(this);

        if ($slider.hasClass('slick-initialized')) {
            setTimeout(() => enforceNoFocus($slider), 0);
            return;
        }

        let settings = $slider.data('slick');

        if (typeof settings === 'string') {
            try {
                settings = JSON.parse(settings);
            } catch (e) {
                console.warn('Parsing error of data-slick:', e);
                settings = {};
            }
        }

        let customPrev = null;
        let customNext = null;

        if (typeof settings.customPrevArrow === 'string') {
            try {
                const $candidate = $(settings.customPrevArrow);
                if ($candidate.length) {
                    customPrev = $candidate.get(0);
                }
            } catch (err) {
                console.warn(`Invalid customPrevArrow selector: "${settings.customPrevArrow}"`, err);
            }
        }

        if (typeof settings.customNextArrow === 'string') {
            try {
                const $candidate = $(settings.customNextArrow);
                if ($candidate.length) {
                    customNext = $candidate.get(0);
                }
            } catch (err) {
                console.warn(`Invalid customNextArrow selector: "${settings.customNextArrow}"`, err);
            }
        }

        settings.prevArrow = customPrev || '<button class="slick-prev slick-arrow" aria-label="Mostra la slide precedente" type="button">Mostra la slide precedente</button>';
        settings.nextArrow = customNext || '<button class="slick-next slick-arrow" aria-label="Mostra la slide successiva" type="button">Mostra la slide successiva</button>';

        $slider.slick(settings);

        // Re-run swatch init now that Slick has built this slider's slides — a bind
        // done before Slick touches the carousel gets silently dropped otherwise
        // (same reason einsteinCarousel.js binds swatches after its own Slick init).
        swatchSelector.initializeClass($slider);

        $slider.slick('getSlick').$dots && $slider.slick('getSlick').$dots.find('button').off('focus');
        $slider.find('[role="tabpanel"]').removeAttr('role');

        const updateCustomArrowVisibility = (slick) => {
            const total = slick.slideCount;
            const visible = slick.options.slidesToShow;
            const infinite = slick.options.infinite;
            const shouldHide = !infinite && total <= visible;

            let $prev = $();
            let $next = $();

            if (typeof settings.customPrevArrow === 'string') {
                try {
                    $prev = $(settings.customPrevArrow);
                } catch (err) {
                    console.warn(`Invalid customPrevArrow in visibility check: "${settings.customPrevArrow}"`, err);
                }
            }

            if (typeof settings.customNextArrow === 'string') {
                try {
                    $next = $(settings.customNextArrow);
                } catch (err) {
                    console.warn(`Invalid customNextArrow in visibility check: "${settings.customNextArrow}"`, err);
                }
            }

            if ($prev.length) $prev.toggleClass('slick-hidden', shouldHide);
            if ($next.length) $next.toggleClass('slick-hidden', shouldHide);
        };

        updateCustomArrowVisibility($slider.slick('getSlick'));

        // Prevent click from firing on swipe gesture
        let isSliding = false;
        $slider.on('beforeChange.swipe', function () { isSliding = true; });
        $slider.on('afterChange.swipe', function () { isSliding = false; });
        $slider.find('a').off('click.swipe').on('click.swipe', function (e) {
            if (isSliding) {
                e.stopImmediatePropagation();
                e.stopPropagation();
                e.preventDefault();
            }
        });

        $slider.on('beforeChange', function (event, slick, currentSlide) {
            const $currentSlide = $(slick.$slides.get(currentSlide));
            $currentSlide.find('*').blur();

            // setSlideClasses() (which sets the new aria-hidden values) runs
            // synchronously right after this event fires, in the same call
            // stack — a same-tick deferral is enough to read its result here,
            // instead of waiting for 'afterChange' below, which only fires once
            // the slide *animation* finishes (autoplay transitions are visibly
            // animated, so that gap is real wall-clock time, not just one tick).
            setTimeout(() => syncSlideTabindex($slider), 0);
        });

        $slider.on('init afterChange setPosition', function (e, slick) {
            setTimeout(() => {
                const $prev = $(slick.$prevArrow);
                const $next = $(slick.$nextArrow);

                [$prev, $next].forEach(($el) => {
                    const isHiddenBySlideCount = slick.slideCount <= slick.options.slidesToShow;
                    const isDisabled = $el.hasClass('slick-disabled');

                    $el.toggleClass('slick-hidden', isHiddenBySlideCount);
                    $el.attr('aria-hidden', false);
                    $el.attr('aria-disabled', isDisabled || isHiddenBySlideCount ? 'true' : 'false');
                    $el.attr('tabindex', isDisabled || isHiddenBySlideCount ? '-1' : '0');
                });

                const $dots = $slider.find('.slick-dots');
                $dots.removeAttr('role');
                $dots.find('li').removeAttr('role');
                $dots.find('button')
                    .removeAttr('role aria-selected aria-controls aria-label')
                    .attr('tabindex', '-1')
                    .attr('aria-hidden', 'true');

                // Safety net for paths that don't fire 'beforeChange' (init,
                // resize/breakpoint changes via setPosition) — real slide
                // transitions are already handled faster, on 'beforeChange' above.
                syncSlideTabindex($slider);

                enforceNoFocus($slider);

                $slider.find('.slick-slide[aria-hidden="true"] svg, .slick-slide[aria-hidden="true"] use')
                    .attr('focusable', 'false')
                    .attr('tabindex', '-1');
            }, 0);
        });
    });
};

const yieldToMain = () => new Promise(resolve => setTimeout(resolve, 0));

const initializeSlidersStaggered = async ($context) => {
    const elements = ($context || $('.js-slider')).filter(':not(.slick-initialized)').toArray();
    for (const el of elements) {
        try {
            initializeSliders($(el));
        } catch (err) {
            console.warn('Slider init failed, skipping to next slider:', err);
        }
        await yieldToMain();
    }
};

const initializeSlidersLazy = ($context) => {
    const $targets = ($context || $('.js-slider')).filter(':not(.slick-initialized)');
    if (!$targets.length) return;

    if (!('IntersectionObserver' in window)) {
        initializeSlidersStaggered($context);
        return;
    }

    const vp = window.innerHeight;
    const immediate = [];
    const deferred = [];

    // Single batch read of all positions before any DOM write,
    // prevents layout thrashing during classification
    $targets.each(function () {
        if (this.getBoundingClientRect().top < vp * 1.5) {
            immediate.push(this);
        } else {
            deferred.push(this);
        }
    });

    // Sliders within 1.5 viewports: staggered immediate init, no flash risk
    if (immediate.length) {
        (async () => {
            for (const el of immediate) {
                try {
                    initializeSliders($(el));
                } catch (err) {
                    console.warn('Slider init failed, skipping to next slider:', err);
                }
                await yieldToMain();
            }
        })();
    }

    // Below-fold sliders: initialized only when within 400px of the viewport.
    // To revert to previous behaviour, replace initializeSlidersLazy()
    // with initializeSlidersStaggered() in responsive.js
    if (deferred.length) {
        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    obs.unobserve(entry.target);
                    initializeSliders($(entry.target));
                }
            });
        }, { rootMargin: '600px 0px' });

        deferred.forEach(el => {
            observer.observe(el);
            // Keyboard accessibility: initialize immediately if focus reaches
            // the slider before it enters the visual viewport
            el.addEventListener('focusin', function () {
                observer.unobserve(el);
                initializeSliders($(el));
            }, { once: true });
        });
    }
};

$(() => {
    $('body').on('reinitSlick', () => {
        if ($('.js-slider').length > 0) {
            initializeSliders();
        }
    });
});

module.exports = { initializeSliders, initializeSlidersStaggered, initializeSlidersLazy };
